import React, { Fragment } from 'react';
import { VolumeOff, VolumeUp } from '@material-ui/icons';
import { EDULAI_BLUE, EDULAI_ORANGE } from '../../styles/styleConsts';
import { connect } from 'react-redux';

/* Props
 * @param {string} text - The text to be spoken.
 * @param {string} language - The language of the text to be spoken.
 * @param {boolean} speak - Imperative prop to trigger speech synthesis.
 * @param {function} Component - The component clickable to trigger speech synthesis.
 */
class SpeechSynthesizer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isSpeaking: false
    };
    this.voices = [];
    this.selectedVoice = null;
    this.speechSynthesis = window.speechSynthesis || window.webkitSpeechSynthesis;
  }

  get language() {
    return this.props.language || this.props.selectedLanguage || 'en';
  }

  componentDidMount() {
    this.speechSynthesis.addEventListener('voiceschanged', this.updateVoices);
    window.addEventListener('beforeunload', this.handleBeforeUnload);
    this.updateVoices();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.text !== this.props.text) {
      if (this.props.speak && !this.state.isSpeaking) {
        this.speak();
        return;
      } else if (this.props.speak && this.state.isSpeaking) {
        this.stop();
        this.speak();
        return;
      } else if (!this.props.speak && this.state.isSpeaking) {
        this.stop();
        return;
      }
    }

    if(prevProps.speak && !this.props.speak){
      this.stop();
    }
  }

  componentWillUnmount() {
    this.speechSynthesis.removeEventListener('voiceschanged', this.updateVoices);
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    this.speechSynthesis.cancel();
  }

  handleBeforeUnload = () => {
    this.speechSynthesis.cancel();
  };

  updateVoices = () => {
    const allVoices = this.speechSynthesis.getVoices();
    const voices = allVoices.filter((v) => v.lang.startsWith(this.language));

    const findVoice = (predicate) => voices.find((v) => predicate(v.name.toLowerCase()));
    let preferredVoice = null;

    // 1. Edge: Microsoft Online (Natural) voices - neural cloud voices, best quality
    //    Prefer female voices (Elsa=IT, Aria/Jenny=EN, Isabella=ES, Laura=PT, Helena=ES, Paloma=ES)
    const femaleEdgeNames = ['elsa', 'aria', 'jenny', 'isabella', 'laura', 'helena', 'paloma'];
    for (const name of femaleEdgeNames) {
      preferredVoice = findVoice((n) => n.includes(name) && n.includes('online') && n.includes('natural'));
      if (preferredVoice) break;
    }
    // Fallback to any Online Natural voice
    if (!preferredVoice) {
      preferredVoice = findVoice((n) => n.includes('online') && n.includes('natural'));
    }

    // 2. Chrome: Google voices - cloud-based, good quality
    if (!preferredVoice) {
      preferredVoice = findVoice((n) => n.includes('google'));
    }

    // 3. Safari/macOS: Apple premium voices (female first)
    if (!preferredVoice) {
      const appleVoices = ['samantha', 'alice', 'federica', 'luca', 'paolo'];
      for (const name of appleVoices) {
        preferredVoice = findVoice((n) => n.includes(name));
        if (preferredVoice) break;
      }
    }

    // 4. Any remote/cloud voice (usually higher quality than local)
    if (!preferredVoice) {
      preferredVoice = voices.find((v) => !v.localService);
    }

    // 5. Firefox/fallback: pick the default or first available voice
    if (!preferredVoice) {
      preferredVoice = voices.find((v) => v.default) || voices[0];
    }

    this.voices = voices;
    this.selectedVoice = preferredVoice;

    if (this.voices.length > 0 && this.props.speak && !this.state.isSpeaking) {
      this.speak();
    }
  };

  stop = (e) => {
    if (e) e.stopPropagation();
    this.speechSynthesis.cancel();
    this.setState({ isSpeaking: false });
  };

  speak = (e) => {
    if (e) e.stopPropagation();
    if (this.speechSynthesis.speaking && !this.state.isSpeaking) {
      this.speechSynthesis.cancel();
    }
    const utterance = new SpeechSynthesisUtterance(this.props.text);
    utterance.voice = this.selectedVoice || this.voices[0];
    utterance.lang = this.selectedVoice ? this.selectedVoice.lang : this.language;

    utterance.onend = () => {
      this.setState({ isSpeaking: false });
    };

    utterance.onerror = (error) => {
      if (error.error !== 'interrupted') {
        console.error('Error synthesis: ', error.error);
      }
      this.setState({ isSpeaking: false });
    };

    utterance.onstart = () => {
      this.setState({ isSpeaking: true });
    };

    this.speechSynthesis.speak(utterance);
  };

  render() {
    return (
      <Fragment>
        {this.props.Component ? (
          this.props.Component
        ) : this.state.isSpeaking ? (
          <VolumeUp
            style={{ color: EDULAI_ORANGE, cursor: 'pointer', width: '100%', padding: 3 }}
            onClick={this.stop}
            className="SpeechSynthesizer"
          />
        ) : (
          <VolumeOff
            style={{ color: EDULAI_BLUE, cursor: 'pointer', width: '100%', padding: 3 }}
            onClick={this.speak}
            className="SpeechSynthesizer"
          />
        )}
      </Fragment>
    );
  }
}

const mapStateToProps = (state) => ({
  selectedLanguage: state.utils.selectedLanguage
});

export default connect(mapStateToProps)(SpeechSynthesizer);
